import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider'; // Import Divider
import { trackTickerSearch } from '../utils/logActivity';
import { defaultTickerForMarket } from '../utils/branding';



function SymbolList({ onSelectSymbol }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [error, setError] = useState(null);
  const { api, user } = useAuth();
  const { config: appConfig } = useConfig();

  /**
   * The market to load symbols for.
   *
   * The signed-in customer's own market wins. appConfig.market is baked into the bundle at build
   * time for the whole deployment, so using it alone meant every user was shown the US list no
   * matter what their profile said — which is why changing a user's market in the admin console
   * appeared to do nothing. It remains the fallback for anonymous visitors and for the moment
   * before the profile has loaded.
   *
   * The backend is authoritative regardless: it serves a signed-in user their own market and
   * ignores this parameter. Sending the right value keeps the request and the response in
   * agreement rather than relying on the server to correct it.
   */
  const userMarketId = user?.marketId;
  const backendMarket =
    userMarketId === 2 ? 'IN'
      : userMarketId === 1 ? 'US'
        : (appConfig?.market === 'IN' ? 'IN' : 'US');

  // Held in a ref so a parent that recreates this callback each render cannot turn every render
  // into another symbol fetch, while the effect still calls the current one.
  const onSelectRef = useRef(onSelectSymbol);
  useEffect(() => { onSelectRef.current = onSelectSymbol; }, [onSelectSymbol]);

  useEffect(() => {
    const fetchSymbols = () => {
      // Deliberately keyed on backendMarket rather than a one-shot `initialized` flag: the
      // profile arrives after the first render, so a fetch that ran only once would always be
      // the pre-profile one and an India customer would keep the US list they were served
      // before their market was known.
      if (!appConfig) return;

      console.log('[SymbolList] loading symbols for market:', backendMarket,
        '(user marketId:', userMarketId, ', deployment default:', appConfig.market, ')');

      api.get(`/api/symbols?market=${backendMarket}`)
        .then(response => {
          setSymbols(response.data);
          setError(null);
          
          // Open on the symbol that belongs to the market that just loaded: SPY for
          // US/International, NSEI for India. appConfig.defaultTicker is a single deployment-wide
          // value (SPY), so an India customer used to land on whatever happened to be first in
          // their list. It is kept only as the fallback for an unrecognised market.
          if (response.data.length > 0) {
            const defaultTicker = defaultTickerForMarket(backendMarket)
              || appConfig.defaultTicker
              || 'SPY';
            
            // Find the default ticker in the list and select it
            const defaultSymbol = response.data.find(symbol => symbol === defaultTicker);
            if (defaultSymbol) {
              setSelected(defaultSymbol);
              if (onSelectRef.current) onSelectRef.current(defaultSymbol);
            } else {
              // If default ticker not found, select the first symbol
              const firstSymbol = response.data[0];
              setSelected(firstSymbol);
              if (onSelectRef.current) onSelectRef.current(firstSymbol);
            }
          }
        })
        .catch(() => {
          setSymbols([]);
          setError('Could not load symbols.');
        });
    };

    fetchSymbols();
  }, [api, appConfig, backendMarket, userMarketId]); // re-fetch when the resolved market changes

  const handleClick = (symbol) => {
    setSelected(symbol);
    if (onSelectSymbol) onSelectSymbol(symbol);

    // Records which symbols customers actually pick, and what they had typed when they
    // picked one. Fire-and-forget: it never blocks the selection.
    const market = backendMarket;
    const matchCount = search
      ? symbols.filter(s => s.toLowerCase().includes(search.toLowerCase())).length
      : null;
    trackTickerSearch(api, symbol, market, matchCount);
  };

  // MUI imports
  // (Place these at the top: import Box from '@mui/material/Box'; import Paper from '@mui/material/Paper'; import TextField from '@mui/material/TextField'; import List from '@mui/material/List'; import ListItem from '@mui/material/ListItem'; import ListItemButton from '@mui/material/ListItemButton'; import ListItemText from '@mui/material/ListItemText'; import Typography from '@mui/material/Typography';)
  return (
    <Box sx={{ width: 112, display: 'flex', flexDirection: 'column', p: 0, m: 0, bgcolor: '#fff', border: '3px solid #1e293b', boxSizing: 'border-box' }}>
      <TextField
        variant="outlined"
        size="small"
        placeholder="Search Symbol..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ m: 1, mb: 0.5, borderRadius: 1, background: '#f8fafc' }}
        inputProps={{ style: { fontSize: 16 } }}
      />
      <Typography sx={{ fontSize: '18px', fontWeight: 700, paddingLeft: '16px', paddingRight: (theme) => theme.spacing(1), py: 1, color: '#0f172a', bgcolor: '#f3f4f6', borderBottom: '1px solid #e5e7eb', textDecoration: 'underline' /* Removed variant and letterSpacing */ }}>
        Symbol
      </Typography>
      {/* This Box is the main scrollable container for the list */}
      <Box sx={{
          direction: 'rtl', // Move scrollbar to the left for WebKit
          overflowY: 'auto',
          width: '100%',
          maxHeight: 'calc(100vh - 150px)',
          minHeight: 0,
          // Explicit scrollbar styling
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: (theme) => theme.palette.background.default, // Or a light grey
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: (theme) => theme.palette.grey[400], // A visible grey
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.grey[500],
            }
          },
          // For Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: (theme) => `${theme.palette.grey[400]} ${theme.palette.background.default}`,
        }}>

        {symbols.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
            {error || 'No symbols found.'}
          </Typography>
        ) : (
          // Removed redundant inner div with overflowY: 'auto'
          <List dense disablePadding sx={{ width: '100%', direction: 'ltr' }}> {/* Reset direction for list content */}
            {symbols.filter(symbol => symbol.toLowerCase().includes(search.toLowerCase())).map(symbol => (
              <React.Fragment key={symbol + '-item'}> {/* Added React.Fragment wrapper */}
                <ListItem disablePadding> {/* Removed key={symbol} from ListItem as Fragment now has a key */}
                  <ListItemButton
                    selected={selected === symbol}
                    onClick={() => handleClick(symbol)}
                    onDoubleClick={() => { if (onSelectSymbol) onSelectSymbol(symbol); }}
                    sx={(theme) => ({
                      // borderRadius: 2, // Removing individual item border radius for a more continuous list look with dividers
                      // mb: 0.5, // Removing margin bottom as dividers will provide separation
                      transition: 'background 0.2s, color 0.2s',
                      '&:hover': { 
                        bgcolor: theme.palette.action.hover, 
                      },
                      '&.Mui-selected': {
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText, // This should make text color contrast with primary.main
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                        }
                      },
                      paddingLeft: '16px', // Align text to start at 33% of the width
                      paddingRight: (theme) => theme.spacing(1), // Keep some right padding
                      py: '4px', // Reduced vertical padding to pack items tighter
                    })}
                  >
                    <ListItemText 
                        primary={symbol} 
                        primaryTypographyProps={{ 
                            fontWeight: 500, 
                            fontSize: 16 
                            // Removed hardcoded color: '#0f172a' to allow inheritance from ListItemButton's selected state
                        }} 
                    />
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
              </React.Fragment> /* Closing React.Fragment wrapper */
            ))}
            </List>
          // Removed redundant inner div
        )}
      </Box>
      {/* Removed redundant bottom bar Box, relying on the main container's border */}
    </Box>
  );
}

SymbolList.propTypes = {
  onSelectSymbol: PropTypes.func,
};

export default SymbolList;
